import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { Chip } from 'src/components/shared/Chip'
import { MAX_VISIBLE_PRODUCT_CHIPS } from 'src/constants/insurance-policy/constants'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'

interface Props {
  policyProducts: InsurancePolicyProduct[]
}

const InsurancePolicyProductsCellComponent = ({ policyProducts }: Props) => {
  const productNames = useMemo(() => {
    return policyProducts.map((policyProduct) => {
      // `insuranceProduct` is left-joined, so a product whose catalog row was deleted arrives
      // without one. Fall back to the raw id rather than inventing a name.
      return policyProduct.insuranceProduct?.name ?? policyProduct.insuranceProductId
    })
  }, [policyProducts])

  if (productNames.length === 0) {
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        No products yet
      </Typography>
    )
  }

  const visibleNames = productNames.slice(0, MAX_VISIBLE_PRODUCT_CHIPS)
  const hiddenCount = productNames.length - visibleNames.length

  return (
    <Tooltip title={productNames.join(' · ')}>
      {/* `overflow-hidden` with non-shrinking chips is what keeps the row on one line: the grid's
          row height is fixed, so wrapping chips get clipped top and bottom instead. */}
      <div css={tw`flex min-w-0 flex-row items-center gap-1 overflow-hidden`}>
        {visibleNames.map((name, index) => {
          return (
            // A policy cannot hold the same product twice, but the index keeps the key stable
            // even if a name ever repeats through the raw-id fallback above.
            <Chip key={`${name}-${index}`} text={name} className={tw`flex-shrink-0`} />
          )
        })}
        {hiddenCount > 0 && (
          <Typography variant="body2" css={tw`flex-shrink-0 text-shades-500`}>
            {`+${hiddenCount}`}
          </Typography>
        )}
      </div>
    </Tooltip>
  )
}

export const InsurancePolicyProductsCell = memo(InsurancePolicyProductsCellComponent)
